import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { compose } from 'redux';
import { Table } from 'reactstrap';
import { get } from 'lodash';
import moment from 'moment';

import Select from 'components/Form/Select';
import TermsModal from 'components/TermsModal';
import LoansAgreementModal from 'components/LoansAgreementModal';
import CancelPaymentModal from 'components/CancelPaymentModal';
import Validator from 'components/Validator/Validator';
import { Button, ButtonLink } from 'components/Button';
import { formatCurrency } from 'utils/formatCurrency';
import { scheduleQuickpayAutopayAction } from 'actions/quickpay';

import scheduleAutopaySchema from './schemas/scheduleAutopaySchema';
import styles from './styles.module.scss';

const EnrollAutopay = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [paymentModal, setPaymentModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [termsModal, setTermsModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const quickpayData = useSelector((state) => state.quickpay.data);
  const {
    validator: {
      values,
      errors,
      onChangeHandler,
      validate,
      setValues,
    },
  } = props;

  const allPaymentMethods = (quickpayData?.paymentAccounts || []).map((acc) => ({
    value: acc.accountId,
    title: `${(acc.accountType)} ${acc.category === 'bankcard' ? `xxxx-xxxx-xxxx-${acc.cardNumberLast4}` : `xxxx${acc.accountNumberLast4}`}`,
  }));

  allPaymentMethods.push({
    value: 'addPaymentMethod',
    title: '+ Add Payment Method ...',
  });

  const frequency = moment(new Date(get(quickpayData, 'suggestedAutopayNextApplyDate', ''))).format('Do');
  const nextDate = get(quickpayData, 'suggestedAutopayNextApplyDate') ? moment(get(quickpayData, 'suggestedAutopayNextApplyDate')).format('MM/DD/YYYY').toString() : '-';

  useEffect(() => {
    const primaryPaymentMethod = (quickpayData.paymentAccounts || []).find((acc) => acc.isPrimary);
    if (primaryPaymentMethod) {
      setValues({
        ...values,
        paymentAccountId: primaryPaymentMethod.accountId,
      });
    }
  }, []);

  const toggleModal = (type) => {
    if (type === 'cancel') {
      setCancelModal(!cancelModal);
      setPaymentModal(false);
    } else if (type === 'payment') {
      setPaymentModal(!paymentModal);
      setCancelModal(false);
    } else {
      setTermsModal(!termsModal);
    }
  };

  const handleInputChange = (event) => {
    switch (event.target.name) {
      case 'payFromAccount':
      case 'paymentAccountId':
        if (event.target.value === 'addPaymentMethod') {
          navigate('/quickpay/add-payment-method');
        } else {
          onChangeHandler(event.target.name, event.target.value);
        }
        break;
      default:
        onChangeHandler(event.target.name, event.target.value);
    }
  };

  const handleScheduleAutopay = () => {
    if (validate().isValid) {
      setErrorMsg('');
      setIsLoading(true);
      dispatch(scheduleQuickpayAutopayAction({
        data: {
          amount: get(quickpayData, 'suggestedAutopayAmount'),
          nextApplyDate: moment(nextDate).format('YYYY-MM-DD'),
          paymentAccountId: values.paymentAccountId,
        },
        success: () => {
          setIsLoading(false);
          navigate('/quickpay/autopay-scheduled');
        },
        fail: (err) => {
          setErrorMsg(get(err, 'data.message') || 'An error occurred.');
          setIsLoading(false);
        },
      }));
    }
  };

  return (
    <div className={styles.quickpay}>
      <div className={styles.content}>
        <h1>Enroll in Autopay</h1>
        <Table className={cn(styles.autopayTable, 'mb-4')} size="sm" responsive striped borderless>
          <tbody>
            <tr>
              <th scope="row" className="pl-3">Frequency</th>
              <td className="text-right pr-3">
                Monthly (Every
                {' '}
                {frequency}
                )
              </td>
            </tr>
            <tr>
              <th scope="row" className="pl-3">Next Date</th>
              <td className="text-right pr-3">{nextDate}</td>
            </tr>
            <tr>
              <th scope="row" className="pl-3">Payment Amount</th>
              <td className="text-right pr-3">
                $
                {get(quickpayData, 'suggestedAutopayAmount') ? formatCurrency(get(quickpayData, 'suggestedAutopayAmount'), 2) : '-'}
              </td>
            </tr>
          </tbody>
        </Table>
        <Select
          name="paymentAccountId"
          data={allPaymentMethods}
          value={values.paymentAccountId}
          onChange={handleInputChange}
          label="Pay From Account"
          hasError={!!errors.paymentAccountId}
          errorMessage={errors.paymentAccountId}
          isRequired
        />
      </div>
      <div className={styles.stickyFooter}>
        {errorMsg ? (
          <div className={styles.error}>
            {errorMsg}
          </div>
        ) : null}
        <small>
          By clicking &#34;SCHEDULE AUTOPAY&#34;, I authorize recurring payments as designated above under these
          {' '}
          <ButtonLink className="d-inline btn-link" style={{ textDecoration: 'underline', fontWeight: 'bold', cursor: 'pointer' }} onClick={() => toggleModal('terms')}>terms</ButtonLink>
          .
        </small>
        <Button
          color="primary"
          className="w-100 mt-1"
          onClick={handleScheduleAutopay}
          isLoading={isLoading}
        >
          SCHEDULE AUTOPAY
        </Button>
        <span
          className={`${styles.anchorBtn} mt-1 d-block`}
          onClick={() => navigate(-1)}
        >
          Cancel
        </span>
        <ButtonLink className="d-block btn-link" style={{ cursor: 'pointer', marginTop: 10 }} onClick={() => toggleModal('payment')}>How will my payments be applied?</ButtonLink>
      </div>
      <LoansAgreementModal
        className={styles.modal}
        isOpen={paymentModal}
        toggle={() => toggleModal('payment')}
        paddingTop={1}
        showCloseBtn
        footerLink={<div style={{ marginTop: 16 }} />}
      />
      <CancelPaymentModal
        className={styles.modal}
        isOpen={cancelModal}
        toggle={() => toggleModal('cancel')}
        paddingTop={1}
        showCloseBtn
        footerLink={<div style={{ marginTop: 16 }} />}
      />
      <TermsModal
        className={`${styles.modal} ${styles.modalTerms}`}
        isOpen={termsModal}
        toggle={() => toggleModal('terms')}
        channel="dtm"
        fee="15"
        showCloseBtn
        paddingTop={1}
      />
    </div>
  );
};

EnrollAutopay.propTypes = {
  validator: PropTypes.object.isRequired,
};

export default compose(Validator(scheduleAutopaySchema))(EnrollAutopay);
